import { ReactNode } from 'react';
import { DEFAULT_PLACEHOLDER } from '@insio/format-utils';
import {
  InvoiceField,
  InvoiceSection,
  VatSummaryField,
  matchIndexedChild
} from '@insio/validation';
import { Card, CardContent } from '../../../../components/Card';
import { Icon } from '../../../../components/icons/Icon';
import { SimpleTable } from '../../../../components/SimpleTable';
import { ErrorEntry } from '../../../../components/errors';
import { useLocale, useTranslation } from '../../../../providers/UIProvider';
import {
  useInvoiceCurrencyCode,
  useVatSummaryCurrencyCode,
  useVatSummaryExchangeRate
} from '../../../../providers/InvoiceProvider';
import { cn, combineIssues, formatExchangeRate, severityStyle } from '../../../../utils';
import type { ValidationIssue } from '../../../../types';
import type { FormatterProps } from '../types/editor-config';
import { computeVatSummaryTotals, toVatSummaryItems } from '../utils/vat-summary';
import { FormatterError } from './FormatterError';
import { useFormatMoney } from '../../../../hooks';

type CellKey = 'vatRate' | 'netAmount' | 'vatAmount';

interface RowIssues {
  row: ValidationIssue[];
  byField: Partial<Record<CellKey, ValidationIssue[]>>;
}

/** Buckets `vatSummary.<i>` issues by the persisted row index `i`; a rate may repeat. */
function groupRowErrors(
  errors: ValidationIssue[] | undefined,
  rowCount: number
): { byIndex: Map<number, RowIssues>; sectionLevel: ValidationIssue[] } {
  const byIndex = new Map<number, RowIssues>();
  const sectionLevel: ValidationIssue[] = [];
  if (!errors?.length) return { byIndex, sectionLevel };
  for (const issue of errors) {
    const path = issue.path ?? '';
    if (
      path === '' ||
      path === InvoiceSection.vatSummary ||
      path === InvoiceField.vatSummaryExchangeRate
    ) {
      sectionLevel.push(issue);
      continue;
    }
    const match = matchIndexedChild(path, InvoiceSection.vatSummary);
    if (!match || match.index >= rowCount) continue;
    let bucket = byIndex.get(match.index);
    if (!bucket) {
      bucket = { row: [], byField: {} };
      byIndex.set(match.index, bucket);
    }
    const child = match.child;
    if (
      child === VatSummaryField.vatRate ||
      child === VatSummaryField.netAmount ||
      child === VatSummaryField.vatAmount
    ) {
      (bucket.byField[child] ??= []).push(issue);
    } else {
      bucket.row.push(issue);
    }
  }
  return { byIndex, sectionLevel };
}

const DEFAULT_CURRENCY = 'CZK';

/**
 * Read-only display of a VAT summary value: card with a breakdown table
 * (rate / base / VAT / incl. VAT) plus a totals footer. Currency comes from
 * `InvoiceProvider` (`vatSummaryCurrencyCode`, falling back to
 * `invoiceCurrencyCode`, then CZK).
 */
export function VatSummaryFormatter({ value, errors }: FormatterProps): ReactNode {
  const t = useTranslation('modules.modals.editVatSummary');

  const vatSummaryCurrencyCode = useVatSummaryCurrencyCode();
  const invoiceCurrencyCode = useInvoiceCurrencyCode();
  const vatSummaryExchangeRate = useVatSummaryExchangeRate();
  const locale = useLocale();

  // Tolerant of either incoming shape (raw item array or full form-data object)
  // — see `toVatSummaryItems` for why mid-save renders can flip the shape.
  const data = computeVatSummaryTotals(toVatSummaryItems(value));
  const currency = vatSummaryCurrencyCode ?? invoiceCurrencyCode ?? DEFAULT_CURRENCY;
  const vatAmount = data.totalAmount.minus(data.totalAmountNoVat);
  // Each item keeps its persisted index — the `i` of `vatSummary.<i>` issue
  // paths — through the display sort, since a rate can appear twice.
  const sortedRows = data.vatSummary
    .map((item, index) => ({ item, index }))
    .sort((a, b) => a.item.vatRate.cmp(b.item.vatRate));

  const { byIndex, sectionLevel } = groupRowErrors(errors, data.vatSummary.length);

  const sectionCombined = combineIssues(sectionLevel);
  const sectionSeverity = severityStyle(sectionCombined?.severity);

  const formatMoney = useFormatMoney(currency);

  // The rate is only meaningful once the recap is reported in a currency other
  // than the invoice's — a stored rate does not keep the line visible. When the
  // invoice currency is unknown we cannot prove the two match, so a stored rate
  // is still shown.
  const showExchangeRate =
    invoiceCurrencyCode != null ? currency !== invoiceCurrencyCode : vatSummaryExchangeRate != null;

  const rightAligned = 'block text-right';

  const renderCell = (
    content: ReactNode,
    cellIssues: ValidationIssue[] | undefined,
    align: 'left' | 'right' = 'right'
  ): ReactNode => {
    const sev = severityStyle(combineIssues(cellIssues)?.severity);
    return (
      <span
        className={cn(
          'flex flex-col gap-2xs text-foreground-base',
          align === 'right' ? 'items-end' : 'items-start',
          sev?.bgColor && cn('rounded px-xs py-2xs', sev.bgColor)
        )}
      >
        <span>{content}</span>
        <FormatterError issues={cellIssues} />
      </span>
    );
  };

  return (
    <Card className={cn('bg-white border-none shadow-none', sectionSeverity?.borderColor)}>
      <CardContent className='p-0 space-y-xs'>
        {sectionCombined && sectionSeverity && (
          <div
            className={cn('flex items-start gap-xs rounded px-xs py-2xs', sectionSeverity.bgColor)}
          >
            <Icon
              name={sectionSeverity.icon}
              className={cn('h-3.5 w-3.5 shrink-0 mt-2xs', sectionSeverity.iconColor)}
              aria-hidden='true'
            />
            <span className={cn('text-xs', sectionSeverity.iconColor)}>
              <ErrorEntry issue={sectionCombined} showActual />
            </span>
          </div>
        )}
        {showExchangeRate && (
          <div className='flex items-baseline justify-between gap-md text-xs'>
            <span className='uppercase font-semibold text-foreground-muted'>
              {t('exchangeRateLabel')}
            </span>
            <span className='text-foreground-base'>
              {vatSummaryExchangeRate && invoiceCurrencyCode
                ? t('exchangeRateLine', {
                    from: invoiceCurrencyCode,
                    rate: formatExchangeRate(vatSummaryExchangeRate.rate, locale),
                    to: currency
                  })
                : DEFAULT_PLACEHOLDER}
            </span>
          </div>
        )}
        <SimpleTable
          headers={[
            <span key='rate' className='uppercase text-xs font-semibold'>
              {t('vatRateLabel')}
            </span>,
            <span key='base' className={`${rightAligned} uppercase text-xs font-semibold`}>
              {t('baseLabel', { currency })}
            </span>,
            <span key='vat' className={`${rightAligned} uppercase text-xs font-semibold`}>
              {t('vatLabel', { currency })}
            </span>,
            <span key='total' className={`${rightAligned} uppercase text-xs font-semibold`}>
              {t('totalInclVatLabel', { currency })}
            </span>
          ]}
          data={sortedRows}
          getRowClassName={({ index }) => {
            const bucket = byIndex.get(index);
            const rowSev = severityStyle(combineIssues(bucket?.row)?.severity);
            return rowSev?.bgColor;
          }}
          renderRow={({ item, index }, idx) => {
            const bucket = byIndex.get(index);
            return [
              renderCell(
                <span className='font-semibold'>{item.vatRate.toString()} %</span>,
                [...(bucket?.row ?? []), ...(bucket?.byField.vatRate ?? [])],
                'left'
              ),
              renderCell(formatMoney(item.netAmount), bucket?.byField.netAmount),
              renderCell(formatMoney(item.vatAmount), bucket?.byField.vatAmount),
              <span key={`total-${idx}`} className={`${rightAligned} text-foreground-base`}>
                {formatMoney(item.netAmount.plus(item.vatAmount))}
              </span>
            ];
          }}
          footer={[
            <span key='total-label' className='font-semibold uppercase text-foreground-base'>
              {t('vatLabels.total')}
            </span>,
            <span key='total-base' className={`${rightAligned} font-semibold text-foreground-base`}>
              {formatMoney(data.totalAmountNoVat)}
            </span>,
            <span key='total-vat' className={`${rightAligned} font-semibold text-foreground-base`}>
              {formatMoney(vatAmount)}
            </span>,
            <span key='total-incl' className={`${rightAligned} font-semibold text-foreground-base`}>
              {formatMoney(data.totalAmount)}
            </span>
          ]}
          NoContentFallback={<p className='italic'>{t('noVatRates')}</p>}
          cellClassName='text-xs'
          scrollable
        />
      </CardContent>
    </Card>
  );
}
